import { ApiDetailFsResponse, ApiExploreFsResponse, ApiFastSintStore, ApiFastSintStoreItem, ApiFastSintStoreStock } from './api-fast-sint.model';

import { DuttiDate, DuttiDateFormat } from '@frontend/common/duttidate';
import { sendObservabilityLog } from '@shared/analytics/ITXObservabilityCore/helper';
import { DetailFsResponse, ExploreFsResponse, FastSintAvailability, FastSintStoreItem, FastSintStore, FastSintTranslationKeys } from '@shared/fast-sint/models/fast-sint.models';

export class FastSintTransformer {
    public static ExploreFromApi(apiExplore: ApiExploreFsResponse): ExploreFsResponse {
        return {
            isFastSint: apiExplore.isFastSint,
        };
    }

    public static DetailFromApi(apiDetail: ApiDetailFsResponse): DetailFsResponse {
        return {
            isFastSint: apiDetail.isFastSint,
            physicalStoresList: apiDetail.physicalStoresList?.filter((store) => {
                try {
                    return store.stockSourceFastSint?.lines.every((line) => {
                        return line.maximumDeliveryCommitmentDate && line.minimumDeliveryCommitmentDate;
                    });
                } catch (error) {
                    sendObservabilityLog(error, 'FastSintTransformer DetailFromApi');
                    return false;
                }
            })
                .map((store) => {
                    return FastSintTransformer.parseStore(store);
                }),
        };
    }

    private static parseStore(store: ApiFastSintStore): FastSintStore {
        const availability = !!store.stockSourceListNoFastSint.length
            ? FastSintAvailability.PARTIAL
            : FastSintAvailability.FULL;
        const dateFastSint = FastSintTransformer.getDateFastSint(store.stockSourceFastSint.lines);
        const dateNoFastSint = FastSintTransformer.getDateNoFastSint(store.stockSourceListNoFastSint);
        const itemsFastSint = FastSintTransformer.parseItems(store.stockSourceFastSint.lines);
        const itemsNoFastSint = FastSintTransformer.parseItems(FastSintTransformer.getNoFastSintItems(store));
        const readyKeys = FastSintTransformer.getKeys(
            itemsFastSint,
            itemsNoFastSint,
            dateFastSint,
            dateNoFastSint
        );
        return {
            id: store.locationId,
            availability,
            itemsFastSint,
            itemsNoFastSint,
            dateFastSint: FastSintTransformer.parseDate(dateFastSint),
            dateNoFastSint: FastSintTransformer.parseDate(dateNoFastSint),
            keys: readyKeys,
        };
    }

    private static getNoFastSintItems(store: ApiFastSintStore) {
        let noFastSintItems: ApiFastSintStoreItem[] = [];
        let ignoreList: string[] = store.stockSourceFastSint.lines.map(line => line.sku);

        for (let stock of store.stockSourceListNoFastSint) {
            for (let item of stock.lines) {
                if (!ignoreList.includes(item.sku)) {
                    noFastSintItems.push(item);
                    ignoreList.push(item.sku);
                }
            }
        }

        return noFastSintItems;
    }

    private static parseItems(items: ApiFastSintStoreItem[]): FastSintStoreItem[] {
        let parsedItems: FastSintStoreItem[] = [];
        if (!items?.length) return [];
        for (let item of items) {
            parsedItems.push(FastSintTransformer.parseItem(item));
        }
        return parsedItems;
    }

    private static parseItem(item: ApiFastSintStoreItem): FastSintStoreItem {
        return {
            sku: item.sku,
            cartItem: null, // Se rellenará en mergeStores, con la info del cart
        };
    }

    private static getItemsOldestDate(items: ApiFastSintStoreItem[], prop: string): DuttiDate {
        let oldest: DuttiDate = null;
        if (!items) return null;
        for (let item of items) {
            if (item[prop]) {
                const current = new DuttiDate(item[prop], DuttiDateFormat.ISO);
                if (!oldest || current.isAfter(oldest)) {
                    oldest = current;
                }
            }
        }
        return oldest;
    }

    private static getOldestDate(dates: DuttiDate[]): DuttiDate {
        let oldest: DuttiDate;
        for (let date of dates) {
            if (!oldest || date.isAfter(oldest)) {
                oldest = date;
            }
        }
        return oldest;
    }

    private static parseDate(date: DuttiDate): string {
        if (date) {
            return date.local().format(DuttiDateFormat.ISO);
        }
        return null;
    }

    private static getDateFastSint(items: ApiFastSintStoreItem[]): DuttiDate {
        return FastSintTransformer.getItemsOldestDate(items, 'maximumDeliveryCommitmentDate');
    }

    private static getDateNoFastSint(stocks: ApiFastSintStoreStock[]): DuttiDate {
        let dates: DuttiDate[] = [];
        if (!stocks.length) return null;
        for (let stock of stocks) {
            dates.push(
                FastSintTransformer.getItemsOldestDate(stock.lines, 'maximumDeliveryCommitmentDate')
            );
        }
        return FastSintTransformer.getOldestDate(dates);
    }

    public static getKeys(
        itemsFastSint: FastSintStoreItem[],
        itemsNoFastSint: FastSintStoreItem[],
        dateFastSint: DuttiDate,
        dateNoFastSint: DuttiDate,
    ): FastSintTranslationKeys {
        let isPartial = !!itemsNoFastSint.length;
        let keys: FastSintTranslationKeys = {
            fastsint: { key: null, params: null },
            nofastsint: { key: null, params: null },
            nofastsintpickup: { key: null, params: null },
            ready: { key: null, params: null },
            remaining: { key: null, params: null },
            noready: { key: null, params: null },
            noreadypickup: { key: null, params: null },
        };

        // DATE FAST SINT
        let dateTextFastSint: 'Day' | 'Today' | 'Tomorrow' = this.getDateTextFastSint(dateFastSint);

        // DATE NO FAST SINT
        let dateTextNoFastSint: '' | 'Day' | 'Today' | 'Tomorrow' = '';
        if (isPartial) {
            dateTextNoFastSint = this.getDateTextNoFastSint(dateNoFastSint);
        }

        // TYPE
        let pickupType: 'Partial' | 'Plural' | 'Single' = this.getPickupType(isPartial, itemsFastSint);

        // Rellenamos params
        const fsDate = this.formatDate(dateTextFastSint, dateFastSint);
        if (isPartial) {
            const fsItems = itemsFastSint.length.toString();
            const noFsItems = itemsNoFastSint.length.toString();
            const totalItems = (itemsFastSint.length + itemsNoFastSint.length).toString();
            const noFsDate = this.formatDate(dateTextNoFastSint, dateNoFastSint);
            keys.ready.params = {
                $0: fsItems,
                $1: totalItems,
                $2: dateFastSint.format(DuttiDateFormat.HOUR_MINUTE),
            };
            keys.noready.params = { $0: noFsItems, $1: totalItems };
            keys.noreadypickup.params = { $0: noFsItems, $1: totalItems, $2: noFsDate };
            keys.nofastsint.params = { $0: noFsDate };
            keys.nofastsintpickup.params = { $0: noFsDate };
            keys.remaining.params = { $0: noFsDate };
        } else {
            keys.ready.params = { $0: fsDate };
        }
        keys.fastsint.params = { $0: fsDate };

        keys.ready.key = `dev.shopCart.fastSint.pickup${pickupType}${dateTextFastSint}`;
        keys.noready.key = `dev.shopCart.fastSint.pickupPartialUnavailable`;
        keys.noreadypickup.key = `dev.shopCart.fastSint.pickupPartial${dateTextNoFastSint}`;
        keys.remaining.key = `dev.shopCart.fastSint.remaining${dateTextNoFastSint}`;
        keys.fastsint.key = `dev.shopCart.fastSint.availability${dateTextFastSint}`;
        keys.nofastsint.key = `dev.shopCart.fastSint.availability${dateTextNoFastSint}`;
        keys.nofastsintpickup.key = `dev.shopCart.fastSint.selectorOption${dateTextNoFastSint}`;
        return keys;
    }

    private static getDateTextFastSint(date: DuttiDate) {
        const daysFastSint = date.local().diff(new DuttiDate(), 'days');
        if (daysFastSint === 0) {
            return 'Today';
        }
        if (daysFastSint === 1) {
            return 'Tomorrow';
        }
        return 'Day';
    }

    private static getDateTextNoFastSint(date: DuttiDate) {
        const daysNoFastSint = date.local().diff(new DuttiDate(), 'days');
        if (daysNoFastSint === 0) {
            return 'Today';
        }
        if (daysNoFastSint === 1) {
            return 'Tomorrow';
        }
        return 'Day';
    }

    private static getPickupType(isPartial: boolean, itemsFastSint) {
        if (isPartial) {
            return 'Partial';
        }
        if (itemsFastSint.length === 1) {
            return 'Single';
        }
        return 'Plural';
    }

    private static formatDate(text: string, date: DuttiDate) {
        if (text === 'Day') {
            return date.local().format(DuttiDateFormat.DAY_MONTH);
        }
        return date.local().format(DuttiDateFormat.HOUR_MINUTE);
    }
}
